import React, { useCallback, useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Icon,
  Input,
  LoadingIcon,
  Stack,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@eluve/components';
import {
  CARE_PLAN_TAB,
  SUMMARY_TAB,
  TRANSCRIPT_TAB,
  toDocumentTabId,
  useAppointmentTabNav,
} from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';

export interface DocumentTab {
  id: string;
  title: string;
  /** Show a spinner on the tab (e.g. while a document is being generated). */
  loading?: boolean;
}

export interface AppointmentTabBarProps {
  summaryDisabled?: boolean;
  transcriptLoading?: boolean;
  summaryLoading?: boolean;
  documents?: DocumentTab[];
  onDocumentDelete?: (documentId: string) => void;
  onDocumentRename?: (documentId: string, newTitle: string) => void;
  carePlanAvailable?: boolean;
  /** Shows a spinner on the Care Plan tab while the draft is still generating. */
  carePlanGenerating?: boolean;
  /** Slot for trailing content (e.g. Create Document button) */
  trailing?: React.ReactNode;
}

export const AppointmentTabBar: React.FC<AppointmentTabBarProps> = ({
  summaryDisabled,
  transcriptLoading,
  summaryLoading,
  documents = [],
  onDocumentDelete,
  onDocumentRename,
  carePlanAvailable,
  carePlanGenerating,
  trailing,
}) => {
  const { t } = useTranslation();
  const { tab, setTab } = useAppointmentTabNav();
  const [deleteTarget, setDeleteTarget] = useState<DocumentTab | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleStartRename = useCallback(
    (doc: DocumentTab) => {
      if (!onDocumentRename) return;
      setEditingId(doc.id);
      setEditValue(doc.title);
      setTimeout(() => inputRef.current?.select(), 0);
    },
    [onDocumentRename],
  );

  const handleFinishRename = useCallback(() => {
    if (editingId && editValue.trim() && onDocumentRename) {
      onDocumentRename(editingId, editValue.trim());
    }
    setEditingId(null);
  }, [editingId, editValue, onDocumentRename]);

  const handleConfirmDelete = () => {
    if (deleteTarget && onDocumentDelete) {
      onDocumentDelete(deleteTarget.id);
    }
    setDeleteTarget(null);
  };

  return (
    <>
      <Stack direction="horizontal" align="center" gap={3}>
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v)}
          className="min-w-0 shrink"
        >
          <TabsList variant="line" className="w-auto">
            <TabsTrigger value={TRANSCRIPT_TAB} className="flex-none">
              {transcriptLoading && <LoadingIcon />}
              {t('appointment.transcriptTab')}
            </TabsTrigger>
            <TabsTrigger
              value={SUMMARY_TAB}
              disabled={summaryDisabled}
              className="flex-none"
            >
              {summaryLoading && <LoadingIcon />}
              {t('appointmentTools.tabs.note')}
            </TabsTrigger>
            {carePlanAvailable && (
              <TabsTrigger value={CARE_PLAN_TAB} className="flex-none">
                {carePlanGenerating && <LoadingIcon />}
                {t('appointmentTools.tabs.carePlan')}
              </TabsTrigger>
            )}
            {documents.map((doc) => (
              <TabsTrigger
                key={doc.id}
                value={toDocumentTabId(doc.id)}
                className="group/doc-tab max-w-[200px] min-w-0"
              >
                {doc.loading && <LoadingIcon />}
                {editingId === doc.id ? (
                  <Input
                    ref={inputRef}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleFinishRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFinishRename();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-auto! w-full! border-0! bg-transparent! p-0! text-sm! shadow-none! focus-visible:ring-0! focus-visible:ring-offset-0!"
                  />
                ) : (
                  <span
                    className="truncate"
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      handleStartRename(doc);
                    }}
                  >
                    {doc.title}
                  </span>
                )}
                {onDocumentDelete && editingId !== doc.id && (
                  <span
                    role="button"
                    tabIndex={0}
                    className="hidden shrink-0 cursor-pointer rounded-sm p-0.5 group-data-[state=active]/doc-tab:block hover:bg-bg-tertiary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(doc);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        setDeleteTarget(doc);
                      }
                    }}
                    aria-label={t('appointmentTools.tabs.deleteDocAria', {
                      title: doc.title,
                    })}
                  >
                    <Icon name="Trash" />
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {trailing && <div className="shrink-0">{trailing}</div>}
      </Stack>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('appointmentTools.tabs.deleteDocumentTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('appointmentTools.tabs.deleteDocumentDescription', {
                title: deleteTarget?.title ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-between">
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleConfirmDelete}
            >
              {t('appointmentTools.documents.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
